import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import { IconButton } from "@/components/ds";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/cn";
import { maskValue, type MaskKind } from "@/lib/mask";
import { copyText } from "@/utils/clipboard";

// Ported from d2d-DLP. ZTA design evaluation D4 = A (F13, F14): secrets and
// sensitive values are masked by default, and a reveal is a per-value,
// per-user opt-in.
//
//   masked    the value through lib/mask.ts. Display-level only: the API
//             still returns the raw value (see the note in lib/mask.ts).
//   reveal    an icon button, shown only to a user who may reveal. ZTA's
//             catalog has no reveal permission yet, so the gate is the
//             console's admin check (usePermissions().isAdmin()).
//   copy      copies exactly what is on screen: the masked form while
//             masked, so a masked value never reaches the clipboard.
//
// AUDIT: D4 asks for every reveal to be written to the audit log. There is
// no endpoint to write an audit entry from the console, so `onReveal` is the
// hook for it and no call site wires it yet; nothing in the UI claims the
// reveal is recorded. Wire it once the backend has the endpoint.
//
// `interactive={false}` draws the masked text alone, with no buttons, for
// cells of a LogSearchTable: that grid is one Tab stop with an arrow-key
// cursor, so a focusable cell would add a Tab stop per row. The event's
// drawer carries the reveal there.

export interface MaskedValueProps {
  value: string | null | undefined;
  kind: MaskKind;
  /** What the value is, for the buttons' names: "source IP" → "Reveal source IP". */
  label: string;
  /** False in grid cells: masked text only, no reveal or copy. */
  interactive?: boolean;
  copyable?: boolean;
  /** Called with the raw value when the user reveals it (for the audit write). */
  onReveal?: (value: string) => void;
  /** Wraps the shown text, e.g. in a link (EntityToken). */
  renderText?: (shown: string) => ReactNode;
  /** Shown when there is no value. */
  empty?: ReactNode;
  className?: string;
  textClassName?: string;
}

export function MaskedValue({
  value, kind, label, interactive = true, copyable = true, onReveal, renderText, empty = "—", className, textClassName,
}: MaskedValueProps) {
  const mayReveal = usePermissions().isAdmin();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const raw = value ?? "";
  const masked = raw ? maskValue(kind, raw) : "";
  const isMasked = masked !== raw;
  const shown = revealed && mayReveal ? raw : masked;

  // A new value starts masked again (a row reused for another event).
  // Reset during render, React's "adjust state when a prop changes" pattern.
  const [prevRaw, setPrevRaw] = useState(raw);
  if (prevRaw !== raw) {
    setPrevRaw(raw);
    setRevealed(false);
  }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const toggle = useCallback(() => {
    setRevealed((r) => {
      if (!r) onReveal?.(raw);
      return !r;
    });
  }, [onReveal, raw]);

  const copy = useCallback(async () => {
    // copyText works over plain http too (Phase 0, F10).
    if (await copyText(shown)) {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1400);
    }
  }, [shown]);

  if (!raw) return <span className={cn("text-[var(--d2d-text-muted)]", className)}>{empty}</span>;

  const text = (
    <span className={cn("min-w-0 truncate font-mono", textClassName)}>
      {renderText ? renderText(shown) : shown}
    </span>
  );
  // Masked is said in words too, not only by the dots.
  const maskedNote = isMasked && shown === masked ? <span className="sr-only"> (masked)</span> : null;

  if (!interactive) {
    return (
      <span className={cn("inline-flex min-w-0 max-w-full items-center", className)} data-masked={isMasked && shown === masked ? "true" : undefined}>
        {text}
        {maskedNote}
      </span>
    );
  }

  return (
    <span
      className={cn("inline-flex min-w-0 max-w-full items-center gap-1", className)}
      data-masked={isMasked && shown === masked ? "true" : undefined}
    >
      {text}
      {maskedNote}
      {isMasked && mayReveal && (
        <IconButton
          size="sm"
          aria-label={`${revealed ? "Hide" : "Reveal"} ${label}`}
          aria-pressed={revealed}
          icon={revealed ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
          onClick={toggle}
        />
      )}
      {copyable && (
        <IconButton
          size="sm"
          aria-label={copied ? `Copied ${label}` : `Copy ${label}${isMasked && shown === masked ? " (masked)" : ""}`}
          icon={copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          onClick={copy}
        />
      )}
      <span className="sr-only" role="status" aria-live="polite">{copied ? `Copied ${label}` : ""}</span>
    </span>
  );
}
